import * as React from 'react'
// libs
import { useDispatch, useSelector } from 'react-redux'
import { change, formValueSelector } from 'redux-form'
// view components
import OptionsList from './components/OptionsList'
import ImportExportMenu from './components/ImportExportMenu'
// views
import { ActionsWrapper, Header, LabelWrapper, Section, Title } from './views'
import { AddButton } from 'components/Modals/PropertyDetailsModal/components/Form/view'
// hooks
import { useShowModal } from 'hooks/index'
// constants
import { Forms, Modals } from 'constants/index'
// types
import { DropResult } from 'react-beautiful-dnd'
import { IField, IFieldOption } from 'types/graphql'

interface Props {
  isDisabled: boolean
  property: IField
  scrollParent: HTMLElement
}

const selector = formValueSelector(Forms.PROPERTY_EDIT_FORM)

const CustomOptions: React.FC<Props> = ({ isDisabled, property, scrollParent }) => {
  const dispatch = useDispatch()

  const { showModal } = useShowModal({ modalType: Modals.PICKLIST_OPTION_MODAL })

  const options = useSelector((state) => selector(state, 'options')) || []
  // export works with what is stored on the property, not with unsaved form changes
  const savedOptions = property.options || []

  const changeField = (value: IFieldOption[]) => dispatch(change(Forms.PROPERTY_EDIT_FORM, 'options', value))

  const removeOption = (id: string) => () => {
    const filteredOptions = options.filter((property) => property.id !== id)

    changeField(filteredOptions)
  }

  const reorder = (list: IFieldOption[], startIndex: number, endIndex: number) => {
    const reorderedList = [...list]

    const [removed] = reorderedList.splice(startIndex, 1)
    reorderedList.splice(endIndex, 0, removed)
    return reorderedList
  }

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result

    if (!destination) {
      return
    }

    if (source.droppableId === destination.droppableId) {
      const reorderedOptions = reorder(options, source.index, destination.index)

      changeField(reorderedOptions)
    }
  }

  // the option modal without an option is in the "add" mode
  const addOption = () => showModal()

  return (
    <Section>
      <Header>
        <Title>Options</Title>
        <ActionsWrapper>
          {!isDisabled && (
            <AddButton data-testid='picklist-add-option' onClick={addOption}>
              + add option
            </AddButton>
          )}
          {(!isDisabled || !!savedOptions.length) && (
            <ImportExportMenu
              isDisabled={isDisabled}
              options={options}
              savedOptions={savedOptions}
              fileName={property.label && `${property.label}_options`}
              onImport={changeField}
            />
          )}
        </ActionsWrapper>
      </Header>
      {!!options.length && (
        <LabelWrapper>
          <div>Label</div>
          <div>Internal Value</div>
        </LabelWrapper>
      )}
      <OptionsList
        options={options}
        isDisabled={isDisabled}
        scrollParent={scrollParent}
        onDragEnd={onDragEnd}
        removeOption={removeOption}
      />
    </Section>
  )
}

export default CustomOptions
